import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ProtobufService } from '../protobuf.service';

// Import the generated protobuf types from the compiled proto files
import { 
  SessionValueStore, 
  SessionValueStoreEntry
} from '@proto/odoo/sessionkv';

@Injectable({
  providedIn: 'root'
})
export class SessionKvService {
  private readonly baseUrl = '/services/sessionkv';

  constructor(private protobufService: ProtobufService) {}

  /**
   * Load the entire session value store available to the currently logged in user
   * GET /services/sessionkv
   * 
   * Response codes:
   * - 200 OK: Success
   * - 401 Unauthorized: Not logged in
   * - 500 Internal Server Error: Something went wrong
   */
  loadSessionStore(): Observable<SessionValueStore> {
    return this.protobufService.get(
      this.baseUrl,
    ).pipe(
      map(response => {
        if (response) {
          return SessionValueStore.decode(response)
        }
      })
    );
  }

  /**
   * Create or update an entry in the session value store
   * PUT /services/sessionkv
   * 
   * Response codes:
   * - 200 OK: Existing entry created or updated
   * - 403 Forbidden: Entry name not permitted (restricted prefix)
   * - 500 Internal Server Error: Something went wrong
   */
  createOrUpdateEntry(entry: SessionValueStoreEntry): Observable<SessionValueStoreEntry> {
    return this.protobufService.put(
      this.baseUrl,
      SessionValueStoreEntry.encode(entry),
    ).pipe(map(() => entry))
  }

  /**
   * Delete an entry from the session value store
   * DELETE /services/sessionkv/{entryName}
   * 
   * The entryName should be the full hierarchical path like:
   * "companies/company-1/contacts/contact-456/portorders/portorder-$ulid"
   * 
   * Response codes:
   * - 200 OK: Entry deleted successfully
   * - 403 Forbidden: Entry name not permitted (restricted prefix)
   * - 404 Not Found: Entry did not exist
   * - 500 Internal Server Error: Something went wrong
   */
  deleteEntry(entryName: string): Observable<void> {
    return this.protobufService.delete(`${this.baseUrl}/${entryName}`);
  }

  /**
   * Helper to find all entries of a specific oneof type
   */
  findEntriesByOneOfCase<T>(
    sessionStore: SessionValueStore,
    oneOfCase: string
  ): Array<{name: string, value: T}> {
    if (!sessionStore.entries) {
      return [];
    }

    return sessionStore.entries
      .filter(entry => entry.value?.$case === oneOfCase)
      .map(entry => ({
        name: entry.name,
        value: (entry.value as any)[oneOfCase] as T
      }));
  }

  /**
   * Generate a new UUID for port orders
   */
  generateUuid(): string {
    const uuid = crypto.randomUUID();
    return uuid;
  }
}
